import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { returnErrors } from "../Redux/ActionCreators/errorAction";
import { getCodingQuestionByID, resetCodingQuestionById, runCodingQuestion } from "../Redux/ActionCreators/testLibraryAction";
import './CodeEditor/editor.scss'
import CodeMirror from "@uiw/react-codemirror";
import { basicSetup } from "codemirror";
import { EditorState } from "@codemirror/state";
import { EditorView, Decoration } from "@codemirror/view";
import { RangeSetBuilder } from "@codemirror/state";

import { python } from "@codemirror/lang-python";
import { javascript } from "@codemirror/lang-javascript";
import { sql } from "@codemirror/lang-sql";

import { MenuItem, Select } from "@mui/material";
import { ThreeDots } from "../Loader/loader";


const TryCodingRunner = () => {
  const dispatch = useDispatch();
  const { questionId } = useParams();

  const { codingQuestionByIdResponse } = useSelector(
    (state) => state.testLibrary
  );

  const question = codingQuestionByIdResponse?.question;

  const [code, setCode] = useState("");
  const [language, setLanguage] = useState(null);
  const [processing, setProcessing] = useState(false);

  const [output, setOutput] = useState("");
  const [outputStatus, setOutputStatus] = useState(null);
  const [compileTestCases, setCompileTestCases] = useState([]);
  const [submit, setSubmit] = useState(false);
  const [compile, setCompile] = useState(false);
  const [compail, setCompail] = useState(false);
  const [runInput, setRunInput] = useState("");
  const [active, setActive] = useState("");
  const [initialCode, setInitialCode] = useState("");
  const [editorKey, setEditorKey] = useState(0);

  const isLoading = !question;

  const visibleTestCases = compileTestCases.filter(tc => tc.hidden === false);
  const firstVisible = visibleTestCases[0];
  const passCount = visibleTestCases.filter(tc => tc.executionStatus === 1).length;
  const totalCount = visibleTestCases.length;

  const allPassed = totalCount > 0 && passCount === totalCount;

  useEffect(() => {
    if (!questionId) return;

    dispatch(resetCodingQuestionById());
    dispatch(getCodingQuestionByID(questionId));
  }, [questionId]);


  const parsedData = useMemo(() => {
    if (!question?.data) return null;
    try {
      return JSON.parse(question.data);
    } catch {
      return null;
    }
  }, [question]);


  const languageOptions = useMemo(() => {
    if (!question) return [];
    return question.languages.map((l) => ({
      label: l.displayLanguage,
      value: l.codingLanguage,
    }));
  }, [question]);

  useEffect(() => {
    if (!language && languageOptions.length > 0) {
      setLanguage(languageOptions[0]);
    }
  }, [languageOptions]);

  const codeMeta = useMemo(() => {
    if (!question || question.version !== 2 || !parsedData?.stubs?.length) return null;

    const { head = "", body = "", tail = "" } = parsedData.stubs[0];
    const normalize = (s) => s.replace(/\r\n/g, "\n");

    const h = normalize(head);
    const b = normalize(body);
    let t = normalize(tail);

    if (t.trim().length === 0) {
      t = "";
    }

    return {
      fullCode: h + b + t,
      headLength: h.length,
      tailLength: t.length,
      bodyStart: h.length,
    };
  }, [question, parsedData]);

  useEffect(() => {
    if (codeMeta) {
      setInitialCode(codeMeta.fullCode);
      setCode(codeMeta.fullCode);
    }
  }, [codeMeta]);

  const handleResetCode = () => {
    setCode(initialCode);
    setEditorKey(prev => prev + 1);
    setOutput("");
    setOutputStatus(null);
    setCompileTestCases([]);
    setRunInput("");
  };


  const lockOutsideBody = (meta) =>
    EditorState.transactionFilter.of((tr) => {
      if (!tr.docChanged) return tr;

      const docLength = tr.startState.doc.length;
      const tailStart = docLength - meta.tailLength;

      let invalid = false;

      tr.changes.iterChanges((from, to) => {
        // Allow edits only inside BODY
        if (from < meta.bodyStart || to > tailStart) {
          invalid = true;
        }
      });

      return invalid ? [] : tr;
    });

    const lockedTheme = EditorView.baseTheme({
      ".cm-locked-line": {
        backgroundColor: "#FFC1074D",
      },
      ".cm-locked-line *": {
        cursor: "not-allowed",
      },
    });

  const lockedDecorations = (meta) =>
    EditorView.decorations.compute([], (state) => {
      const builder = new RangeSetBuilder();
      const doc = state.doc;

      const tailStart = doc.length - meta.tailLength;

      // ----- HEAD -----
      let line = doc.lineAt(0);
      while (line.from < meta.bodyStart) {
        builder.add(
          line.from,
          line.from,
          Decoration.line({ class: "cm-locked-line" })
        );
        if (line.number === doc.lines) break;
        line = doc.line(line.number + 1);
      }

      // ----- TAIL -----
      if (meta.tailLength > 0) {
        line = doc.lineAt(tailStart);
        while (line.from <= doc.length) {
          builder.add(
            line.from,
            line.from,
            Decoration.line({ class: "cm-locked-line" })
          );
          if (line.number === doc.lines) break;
          line = doc.line(line.number + 1);
        }
      }

      return builder.finish();
    });



  const cmLanguage = useMemo(() => {
    if (language?.value === "python") return python();
    if (language?.value === "sql") return sql();
    return javascript();
  }, [language]);


  const handleRun = async () => {
    if (!language?.value) {
      returnErrors(dispatch, "Please select language", 500);
      return;
    }

    setProcessing(true);
    setCompile(true);
    setCompail(true);
    setActive("run");
    setRunInput("");
    setOutput("");
    setCompileTestCases([]);
    setOutputStatus(null);

    const payload = {
      questionId: question.id,
      sourceCode: code,
      language: language.value,
      input: runInput || "",
    };

    try {
      const res = await dispatch(runCodingQuestion(payload));

      if (res?.type === "RUN_CODING_QUESTION") {
        const oc = res.payload?.ocResponse || {};
        const testCases = res.payload?.testCases || [];

        setOutputStatus({
          ...oc,
          stdout: oc.stdout ?? oc.actualOutput ?? "",
          actualOutput: oc.actualOutput ?? null,
          input: oc.input ?? null,
          useInput: oc.useInput ?? false,
          executionStatus: oc.executionStatus ?? 0,
          executionTime: oc.executionTime ?? null,
        });

        setCompileTestCases(testCases);
        setRunInput(oc.input || "");
        setOutput(oc.stdout || "");
      }
    } finally {
      setCompail(false);
      setProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{width:'100vw', height:'100vh'}}>
        <div className="loading-overlay" style={{alignItems:'start', color:'#F75891', height:'auto'}}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading job summary...</span>
          </div>
        </div>
      </div>
    );
  }

  if (question.questionTypeID === 13) return <div>Notebook questions not supported</div>;

  return (
    <div className="d-flex align-items-center justify-content-center"style={{margin:'auto', minHeight:'100vh', maxWidth:'100vw', overflowX:'hidden'}}>
      <div className="coding-container" style={{width:'100%'}}>
        <div className="d-flex" style={{ border: "1px solid #D7DADB" }}>
            <Fragment>
              <div
                className="col-md-5"
                ref={(el) => {
                   if (el) {
                     const savedScrollTop = sessionStorage.getItem("tryRunnerScrollTop");
                     const savedScrollLeft = sessionStorage.getItem("tryRunnerScrollLeft");
                     if (savedScrollTop) el.scrollTop = parseInt(savedScrollTop);
                     if (savedScrollLeft) el.scrollLeft = parseInt(savedScrollLeft);
                   }
                }}
                onScroll={(e) => {
                  sessionStorage.setItem("tryRunnerScrollTop", e.target.scrollTop);
                  sessionStorage.setItem("tryRunnerScrollLeft", e.target.scrollLeft);
                }}
                style={{
                  padding: 15,
                  borderRight: "2px solid #D7DADB",
                  maxHeight: "73vh",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <p style={{ marginLeft: 8, fontSize: 18, color: "#555" }}>
                      {question.version === 1 ?  "Database" : "Coding"}
                    </p>
                  </div>
                </div>
                <h2>{question.name}</h2>
              <div dangerouslySetInnerHTML={{ __html: question.description }} />
              </div>
              <div className="col-md-7">
                {!submit && (
                  <Fragment>
                    <div className="lang-dd">
                      <div
                        className="d-flex align-items-center"
                        style={{ width: "40%" }}
                      >
                        <label className="col-form-label">Language</label>
                        &nbsp;&nbsp;
                        <div style={{ width: "70%" }}>
                          <Select
                            fullWidth
                            value={language?.value || ""}
                            onChange={(e) => {
                              const selected = languageOptions.find(
                                (l) => l.value === e.target.value
                              );
                              setLanguage(selected || null);
                            }}
                          >
                            {languageOptions.map((l) => (
                              <MenuItem key={l.value} value={l.value}>
                                {l.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </div>
                      </div>
                        <div>
                          <img
                            src="/images/reset.svg"
                            alt=""
                            className="cursor-pointer"
                            onClick={handleResetCode}
                          />
                        </div>
                    </div>
                    <div style={{ borderBottom: "2px solid #D7DADB" }}></div>
                    <div className="mt-2">
                      {question.version === 2 ? (
                      <CodeMirror
                        key={editorKey}
                        value={code}
                        height="60vh"
                        extensions={[
                          basicSetup,
                          cmLanguage,
                          lockOutsideBody(codeMeta),
                          lockedDecorations(codeMeta),
                          lockedTheme,
                        ]}
                        onChange={(value) => setCode(value)}
                      />
                    ) : (
                      <Editor
                        height="60vh"
                        width={`100%`}
                        language={language?.value || "javascript"}
                        value={code}
                        theme="vs-light"
                        onChange={(v) => setCode(v || "")}
                        options={{
                          autoIndent: "full",
                          contextmenu: true,
                          fontFamily: "monospace",
                          lineHeight: 24,
                          hideCursorInOverviewRuler: true,
                          inlineSuggest: true,
                          fontSize: "14px",
                          formatOnType: true,
                          autoClosingBrackets: true,
                          minimap: { enabled: false },
                          scrollbar: {
                            horizontalSliderSize: 4,
                            verticalSliderSize: 18,
                          },
                          selectOnLineNumbers: true,
                          roundedSelection: false,
                          cursorStyle: "line",
                          automaticLayout: true,
                        }}
                      />
                    )}

                      <div className="run-code-view">
                        <div className="run-header">
                          <button
                            className="cursor-pointer submit-btn"
                            disabled={processing}
                            onClick={handleRun}
                            style={{background: (processing) ? '#D3D3D3' : '', cursor: (processing) ? "not-allowed" : "pointer",}}
                          >
                            COMPILE AND RUN CODE
                          </button>
                        </div>
                        {compile ? (
                        <div className="complie-window" style={{ minHeight: "200px",}}>
                          {compail && (
                            <div
                              style={{
                                padding: 20,
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                fontSize: 18,
                                color: "#555",
                              }}
                            >
                              <ThreeDots color="#009199" />
                              <span>Compiling and running your code…</span>
                            </div>
                          )}
                          {active === "run" && outputStatus && (
                            <div>
                              <h4 style={{ marginBottom: 10 }}>Compile Result</h4>

                              {outputStatus.useInput && runInput && (
                                <div
                                  style={{
                                    marginBottom: 12,
                                    padding: 10,
                                    borderRadius: 6,
                                    background:
                                      outputStatus.executionStatus === 1
                                        ? "#E6FFE6"
                                        : "#FFE6E6",
                                    border:
                                      outputStatus.executionStatus === 1
                                        ? "1px solid #2E8B57"
                                        : "1px solid #B22222",
                                    fontFamily: "monospace",
                                    whiteSpace: "pre-wrap",
                                    maxHeight:'200px',
                                    maxWidth: "100%",
                                    overflow: "auto",
                                  }}
                                >
                                  <strong>Input</strong>
                                  <pre style={{ margin: "6px 0 0 0" }}>{runInput}</pre>
                                </div>
                              )}

                              <div
                                style={{
                                  marginBottom: 12,
                                  padding: 10,
                                  borderRadius: 6,
                                  background:
                                    outputStatus.executionStatus === 1
                                      ? "#E6FFE6"
                                      : "#FFE6E6",
                                  border:
                                    outputStatus.executionStatus === 1
                                      ? "1px solid #2E8B57"
                                      : "1px solid #B22222",
                                  fontFamily: "monospace",
                                  whiteSpace: "pre-wrap",
                                  maxHeight:'200px',
                                  maxWidth: "100%",
                                  overflow: "auto",
                                }}
                              >
                                <strong>Output</strong>
                                <pre style={{ margin: "6px 0 0 0", whiteSpace: "pre", minWidth: "max-content" }}>
                                  {outputStatus.stdout || outputStatus.actualOutput || "—"}
                                </pre>
                              </div>

                              <div
                                style={{
                                  fontWeight: "bold",
                                  color:
                                    outputStatus.executionStatus === 1 ? "green" : "red",
                                }}
                              >
                                Status:{" "}
                                {outputStatus.executionStatus === 1
                                  ? "Success"
                                  : "Failed"}
                              </div>
                            </div>
                          )}

                          <div style={{ fontFamily: "monospace", marginTop: 10 }}>

                          {/* Summary line */}
                          {compileTestCases.length > 0 && (
                            <div
                              style={{
                                color: allPassed ? "#1B8E3E" : "#B00020",
                                fontWeight: 600,
                                fontSize: 16,
                                marginBottom: 12
                              }}
                            >
                              {allPassed
                                ? `✓ All sample testcases passed! (${passCount}/${totalCount})`
                                : `✗ Some sample testcases failed! (${passCount}/${totalCount})`}
                            </div>
                          )}

                          {/* Each testcase status */}
                          {compileTestCases.map((tc) => (
                            <div key={tc.testCaseId} style={{ marginBottom: 6 }}>
                              <strong>Testcase {tc.testCaseId} :</strong>{" "}
                              <span style={{ color: tc.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>
                                {tc.executionStatus === 1 ? "Passed" : "Failed"}
                              </span>
                            </div>
                          ))}

                          {/* Details only for visible testcase */}
                          {firstVisible && (
                            <>
                              <div style={{ marginTop: 14 }}>
                                <div style={{ marginBottom: 6 }}>
                                  <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>Status</strong>{" "}
                                  <span style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>
                                    {firstVisible.executionStatus === 1 ? "Passed" : "Failed"}
                                  </span>
                                </div>

                                <div style={{ marginBottom: 6 }}>
                                  <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>Execution time</strong>{" "}
                                  <span style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>{outputStatus?.executionTime || "—"} ms</span>
                                </div>

                                <div style={{ marginBottom: 10 }}>
                                  <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>Description</strong>{" "}
                                  <span style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>
                                    {firstVisible.executionStatus === 1
                                      ? "Testcase passed!"
                                      : "Output did not match expected result"}
                                  </span>
                                </div>

                                {firstVisible.input && (
                                  <div style={{ marginBottom: 10 }}>
                                    <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>Input</strong>
                                    <pre style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>{firstVisible.input}</pre>
                                  </div>
                                )}

                                <div style={{ marginBottom: 6 }}>
                                  <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}> Output</strong>
                                  <pre style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>{firstVisible.actualOutput || "—"}</pre>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        </div>
                      ) : null}
                      </div>
                    </div>
                  </Fragment>
                )} 
              </div>
            </Fragment>
        </div>
      </div>
    </div>
  );
};

export default TryCodingRunner;